import type {
  DirectoryResolution,
  DirectoryUser,
  Role,
  RoleAssignment,
  RoleCatalog,
} from "@/api/access";
import type { Capability } from "@/api/hooks";

/**
 * The ONE presentation mapping for the Editor role vocabulary.
 *
 * Every map here is written `satisfies Record<Role, …>` (the `labels.ts`
 * rule), so a role added to or renamed in the contract fails the build at the
 * map rather than rendering a raw slug on an administrator's screen — and
 * `editorAccess.test.ts` reads the vocabulary back out of the committed
 * OpenAPI document, so the runtime list below cannot quietly stop covering it.
 *
 * What is deliberately NOT here: what a role may DO. That is the service's
 * matrix, published on `GET /v1/role-catalog`, and everything in this file
 * that reasons about capabilities or redundancy reads that document. The
 * sentences are presentation; the facts they describe arrive from the server.
 */

/**
 * The stable wire values, in the order the screens list them: the two
 * authoring roles, then the review role that includes both of them, then the
 * administrator. `satisfies Record<Role, true>` is the exhaustiveness guard.
 */
const ROLE_SET = {
  "library-author": true,
  "project-editor": true,
  "reviewer-sign-off": true,
  admin: true,
} satisfies Record<Role, true>;

export const ROLE_ORDER = Object.keys(ROLE_SET) as readonly Role[];

export function isRole(value: string): value is Role {
  return (ROLE_ORDER as readonly string[]).includes(value);
}

/** Product names. `reviewer-sign-off` is the stable wire value for Clinical Manager. */
export const ROLE_LABEL = {
  "library-author": "Library Author",
  "project-editor": "Project Editor",
  "reviewer-sign-off": "Clinical Manager",
  admin: "Administrator",
} satisfies Record<Role, string>;

/** The grant dialog's one-sentence description per role. */
export const ROLE_DESCRIPTION = {
  "library-author":
    "Can view projects and manage the shared Clinical Library. Cannot edit or publish projects.",
  "project-editor":
    "Can create, edit, validate, preview and request publication of projects. Cannot clinically sign off or request promotion.",
  "reviewer-sign-off":
    "Includes Library Author and Project Editor access, and can accept an exact patient preview and request its promotion. This role grants no GitHub authority; PR approval remains a separate action by an eligible, different reviewer.",
  admin:
    "Can manage Editor access, perform Editor administration and restore archived Editor resources. This does not grant production deployment authority outside the Editor.",
} satisfies Record<Role, string>;

/** Declaration order, always — the wire order the service uses too. */
export function canonicalRoles(roles: Iterable<Role>): Role[] {
  const chosen = new Set(roles);
  return ROLE_ORDER.filter((role) => chosen.has(role));
}

/** One table row: a person and everything they hold, live and historical. */
export interface SubjectAccessRow {
  subject: string;
  /** The live roles, in canonical order. */
  roles: Role[];
  /** The live grants — what the next edit sends back as `expectedAssignmentIds`. */
  assignments: RoleAssignment[];
  /** The retained revoked grants for this person. */
  history: RoleAssignment[];
  /** The newest grant or revocation timestamp across every row. */
  lastChangedAt: string;
}

export interface GroupedAssignments {
  /** People with at least one live grant — the access table. */
  live: SubjectAccessRow[];
  /** Every revoked grant, newest revocation first — the history view. */
  revoked: RoleAssignment[];
  /** Every identifier worth resolving: subjects, granting and revoking actors. */
  subjects: string[];
}

function newest(values: Array<string | null | undefined>): string {
  let best = "";
  for (const value of values) {
    if (value && value > best) {
      best = value;
    }
  }
  return best;
}

/**
 * One row per Entra subject, never one per assignment record.
 *
 * The backend legitimately holds several live rows per person (Library
 * Author beside Project Editor, say), so the table groups them into chips.
 * A subject whose every grant is revoked is NOT a row here — that is the
 * history view's subject — but their revoked rows are, so "last changed" and
 * the per-person history dialog see the whole record.
 */
export function groupAssignmentsBySubject(
  items: readonly RoleAssignment[],
): GroupedAssignments {
  const bySubject = new Map<string, RoleAssignment[]>();
  for (const item of items) {
    const rows = bySubject.get(item.subject) ?? [];
    rows.push(item);
    bySubject.set(item.subject, rows);
  }

  const live: SubjectAccessRow[] = [];
  for (const [subject, rows] of bySubject) {
    const active = rows.filter((row) => !row.revokedAt);
    if (active.length === 0) {
      continue;
    }
    const roles = canonicalRoles(
      active.map((row) => row.role).filter((role): role is Role => isRole(role)),
    );
    live.push({
      subject,
      roles,
      assignments: active,
      history: rows.filter((row) => Boolean(row.revokedAt)),
      lastChangedAt: newest(
        rows.flatMap((row) => [row.grantedAt, row.revokedAt]),
      ),
    });
  }

  const revoked = items
    .filter((row) => Boolean(row.revokedAt))
    .sort((a, b) => (b.revokedAt ?? "").localeCompare(a.revokedAt ?? ""));

  const subjects = new Set<string>();
  for (const item of items) {
    subjects.add(item.subject);
    if (!isSystemActor(item.grantedBy)) {
      subjects.add(item.grantedBy);
    }
    if (item.revokedBy && !isSystemActor(item.revokedBy)) {
      subjects.add(item.revokedBy);
    }
  }

  return { live, revoked, subjects: [...subjects].sort() };
}

/**
 * The bootstrap job and the local access controller stamp `granted_by` with a
 * sentinel no request can produce. Not a person, so never sent to a directory.
 */
export function isSystemActor(subject: string): boolean {
  return subject.startsWith("system:");
}

/**
 * The roles a selection makes redundant, each mapped to the selected role
 * that already includes it — read off the server's catalog, never inferred.
 *
 * Empty without a catalog: the service refuses a redundant set on its own,
 * so a browser that has not read the policy yet offers no second opinion.
 */
export function redundantSelections(
  selected: readonly Role[],
  catalog: RoleCatalog | undefined,
): Map<Role, Role> {
  const result = new Map<Role, Role>();
  if (!catalog) {
    return result;
  }
  const includes = new Map<Role, Set<Role>>(
    catalog.roles.map((entry) => [entry.role, new Set(entry.includes)]),
  );
  for (const role of selected) {
    for (const other of selected) {
      if (other !== role && includes.get(other)?.has(role)) {
        result.set(role, other);
        break;
      }
    }
  }
  return result;
}

/** The roles a given role already includes, per the catalog. */
export function rolesIncludedBy(
  role: Role,
  catalog: RoleCatalog | undefined,
): Role[] {
  return canonicalRoles(
    catalog?.roles.find((entry) => entry.role === role)?.includes ?? [],
  );
}

/** The union of the catalog's capabilities over a role set. */
export function effectiveCapabilities(
  roles: readonly Role[],
  catalog: RoleCatalog | undefined,
): Set<Capability> {
  const result = new Set<Capability>();
  if (!catalog) {
    return result;
  }
  for (const entry of catalog.roles) {
    if (roles.includes(entry.role)) {
      for (const capability of entry.capabilities) {
        result.add(capability);
      }
    }
  }
  return result;
}

/**
 * Plain-language phrases keyed on the capability that EARNS them. Ordered
 * from the broadest act to the narrowest so the sentence reads as an
 * escalation. Presentation only: which capabilities a role carries is the
 * catalog's answer.
 */
const CAPABILITY_PHRASE: ReadonlyArray<{
  capability: Capability;
  phrase: string;
}> = [
  { capability: "library.write", phrase: "manages the shared Clinical Library" },
  {
    capability: "draft.write",
    phrase: "creates, edits, validates and previews projects",
  },
  {
    capability: "signoff.create",
    phrase: "accepts patient previews and requests promotion",
  },
];

export const NO_ACCESS_SUMMARY = "No Editor access.";
export const ADMINISTRATOR_SUMMARY =
  "Full Editor administration: manages access, restores archived resources, and holds every authoring and review capability.";

function sentence(phrases: string[]): string {
  const [first, ...rest] = phrases;
  if (first === undefined) {
    return "";
  }
  const capitalised = first.charAt(0).toUpperCase() + first.slice(1);
  return `${[capitalised, ...rest].join("; ")}.`;
}

/**
 * A short description of what a role set lets a person do, derived from the
 * EFFECTIVE capabilities the catalog reports for it.
 *
 * Falls back to the role labels while the catalog is still loading, and to
 * "read-only" when the union carries no write capability — a state no role
 * produces today, kept so a future narrow role renders a true sentence rather
 * than an empty one.
 */
export function accessSummary(
  roles: readonly Role[],
  catalog: RoleCatalog | undefined,
): string {
  if (roles.length === 0) {
    return NO_ACCESS_SUMMARY;
  }
  if (!catalog) {
    return `${canonicalRoles(roles).map((role) => ROLE_LABEL[role]).join(", ")}.`;
  }
  const capabilities = effectiveCapabilities(roles, catalog);
  if (capabilities.has("authz.manage")) {
    return ADMINISTRATOR_SUMMARY;
  }
  const phrases = CAPABILITY_PHRASE.filter(({ capability }) =>
    capabilities.has(capability),
  ).map(({ phrase }) => phrase);
  if (phrases.length === 0) {
    return capabilities.has("project.read") || capabilities.has("library.read")
      ? "Read-only access to projects and the Clinical Library."
      : NO_ACCESS_SUMMARY;
  }
  return sentence(phrases);
}

/** What the table and dialogs know about one identifier. */
export interface SubjectIdentity {
  subject: string;
  /** The directory's display name, the session's for the caller, or null. */
  displayName: string | null;
  email: string | null;
  /** True when a directory (or the caller's own session) vouched for the name. */
  resolved: boolean;
  /** The signed-in administrator's own identifier. */
  self: boolean;
  /** A `system:*` actor — a job, not a person. */
  system: boolean;
}

export const UNKNOWN_ENTRA_USER = "Unknown Entra user";

/**
 * Describe an identifier from the directory answer and the caller's session.
 *
 * The session is a legitimate source for exactly ONE row — the caller's own,
 * matched on the object id — and for nobody else: it says who is signed in,
 * not who anybody else is. Everything unresolved renders as
 * "Unknown Entra user" with the whole identifier beside it; nothing here
 * guesses a name from a similar one.
 */
export function describeSubject(
  subject: string,
  resolution: DirectoryResolution | undefined,
  self: { subject: string; displayName: string },
): SubjectIdentity {
  const isSelf = subject === self.subject;
  if (isSystemActor(subject)) {
    return {
      subject,
      displayName: null,
      email: null,
      resolved: false,
      self: false,
      system: true,
    };
  }
  const user = directoryUserFor(subject, resolution);
  if (user) {
    return {
      subject,
      displayName: user.displayName,
      email: user.email ?? null,
      resolved: true,
      self: isSelf,
      system: false,
    };
  }
  if (isSelf) {
    return {
      subject,
      displayName: self.displayName,
      email: null,
      resolved: true,
      self: true,
      system: false,
    };
  }
  return {
    subject,
    displayName: null,
    email: null,
    resolved: false,
    self: false,
    system: false,
  };
}

export function directoryUserFor(
  subject: string,
  resolution: DirectoryResolution | undefined,
): DirectoryUser | null {
  return (
    resolution?.items.find((item) => item.subject === subject)?.user ?? null
  );
}

/** The name a sentence uses for a person: their display name, or their identifier. */
export function identityName(identity: SubjectIdentity): string {
  return identity.displayName ?? identity.subject;
}

/**
 * Rows by display name where one is known, identifiers after, so a table of
 * people reads alphabetically and the unresolved ones sit together at the end.
 */
export function sortRowsByIdentity(
  rows: readonly SubjectAccessRow[],
  identityOf: (subject: string) => SubjectIdentity,
): SubjectAccessRow[] {
  return [...rows].sort((a, b) => {
    const left = identityOf(a.subject);
    const right = identityOf(b.subject);
    if (left.displayName && !right.displayName) return -1;
    if (!left.displayName && right.displayName) return 1;
    return identityName(left).localeCompare(identityName(right));
  });
}

/** Free-text filter over what the table shows for a row. */
export function rowMatches(
  row: SubjectAccessRow,
  identity: SubjectIdentity,
  search: string,
): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  return [
    identity.displayName ?? "",
    identity.email ?? "",
    row.subject,
    ...row.roles.map((role) => ROLE_LABEL[role]),
  ].some((value) => value.toLowerCase().includes(needle));
}
